import { addReport } from '@/api/evaluation'
import {
  getAPTList,
  getAttackTypes,
  getATTCK,
  getChartsData,
  getRequest,
  getTagTreeSnapshotOptions,
  getTaskInfo,
  updateResourceInterceptStatus,
} from '@/api/web-create-task'
import { deleteList, getListDetailsElement } from '@/api/web-list-task'
import {
  getAttackDefenseRanking,
  getRiskLevelDistribution,
  getResourceLevelsRanking,
  getTemplateLoseEfficacyRanking,
  getVerifyDistribution,
  getDeviceProtectionList,
  getResourceFilterData,
  getOfflineLogImportInfo,
  deleteOfflineLogImportRecord,
} from '@/api/web-task-detail'
import { Message } from '@/components/message'

import { useRequest } from 'ahooks'

// 任务信息 任务进度
export const useGetResultInfo = () => {
  const { runAsync: run, data } = useRequest(params => getTaskInfo(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || '获取任务进度失败')
    },
  })

  return [run, data?.data || {}] as const
}

// 获取用例列表
export const useGetUseCaseList = () => {
  const { runAsync: run, loading } = useRequest(params => getListDetailsElement(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || '获取用例列表失败')
    },
  })

  return [run, loading] as const
}

// 获取攻击类型
export const useGetAttackType = () => {
  const { runAsync: run, data } = useRequest(params => getAttackTypes(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || '获取攻击类型失败')
    },
  })

  return [run, data?.data?.attackTypeList || []] as const
}

// 获取ATT&CK
export const useGetAttck = () => {
  const { runAsync: run, data } = useRequest(params => getATTCK(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || '获取att&ck失败')
    },
  })

  return [run, data?.data?.tactics || []] as const
}

// 获取用例标签列表
export const useGetTagTreeOptions = () => {
  const { runAsync: run, data } = useRequest(params => getTagTreeSnapshotOptions(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || '获取标签列表失败')
    },
  })

  return [run, data?.data?.categoryList || []] as const
}

// 更新资源拦截状态
export const useUpdateResourceInterceptStatus = () => {
  const { runAsync: run, loading } = useRequest(params => updateResourceInterceptStatus(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || '更新资源拦截状态失败')
    },
  })

  return [run, loading] as const
}

// 删除任务
export const useDeleteList = () => {
  const { runAsync: run } = useRequest(params => deleteList(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || '删除任务失败')
    },
  })

  return [run] as const
}

// 生成报表
export const useAddReport = () => {
  const { runAsync: run, loading } = useRequest(params => addReport(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || '报表添加失败')
    },
  })

  return [run, loading] as const
}

// 获取攻击组织 （筛选）
export const useGetAPTList = () => {
  const { runAsync: run, data } = useRequest(() => getAPTList(), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || '获取攻击组织失败')
    },
  })

  return [run, data?.data?.items.map(item => ({ value: item.id, label: item.name })) || []] as const
}

// 获取图表数据
export const useGetChartData = () => {
  const {
    runAsync: run,
    data,
    loading,
  } = useRequest(params => getChartsData(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || '获取图表数据失败')
    },
  })

  return [run, data?.data || {}, loading] as const
}

// 抽屉详情
export const useGetResultPluginDetail = () => {
  const {
    runAsync: run,
    data,
    loading,
  } = useRequest(datas => getRequest(datas), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || '获取插件详情失败')
    },
  })

  return [run, data?.data || {}, loading] as const
}

// 攻击防护排名
export const useGetAttackDefenseRanking = () => {
  const { runAsync: run, data } = useRequest(params => getAttackDefenseRanking(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || '获取攻击防护排名失败')
    },
  })

  return [run, data?.data?.items || []] as const
}

// 危险等级分布
export const useGetRiskLevelDistribution = () => {
  const { runAsync: run, data } = useRequest(params => getRiskLevelDistribution(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || '获取危险等级分布失败')
    },
  })

  return [run, data?.data?.items || []] as const
}

// 用例等级分布
export const useGetResourceLevelsRanking = () => {
  const { runAsync: run, data } = useRequest(params => getResourceLevelsRanking(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || '获取用例等级分布失败')
    },
  })

  return [run, data?.data?.list || []] as const
}

// 模板防护失效率排名
export const useGetTemplateLoseEfficacyRanking = () => {
  const { runAsync: run, data } = useRequest(params => getTemplateLoseEfficacyRanking(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || '获取模板防护失效率排名失败')
    },
  })

  return [run, data?.data?.list || []] as const
}

// 验证结果分布
export const useGetVerifyDistribution = () => {
  const { runAsync: run, data } = useRequest(params => getVerifyDistribution(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || '获取验证结果分布失败')
    },
  })

  return [run, data?.data?.items || []] as const
}

// 设备防护列表
export const useGetDeviceProtectionList = () => {
  const { runAsync: run, data } = useRequest(params => getDeviceProtectionList(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || '获取设备防护列表失败')
    },
  })

  return [run, data?.data?.devices || []] as const
}

// 获取筛选聚合数据
export const useGetResourceFilterData = () => {
  const { runAsync: run, data } = useRequest(params => getResourceFilterData(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || '获取筛选聚合数据失败')
    },
  })
  return [run, data?.data || {}] as const
}

// 获取离线日志导入信息
export const useGetOfflineLogImportInfo = () => {
  const { runAsync: run, data } = useRequest(params => getOfflineLogImportInfo(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || '获取离线日志导入信息失败')
    },
  })

  return [run, data?.data || {}] as const
}

// 删除离线日志导入信息
export const useDeleteOfflineLogImportInfo = () => {
  const { runAsync: run, loading } = useRequest(params => deleteOfflineLogImportRecord(params), {
    manual: true,
    onError: (err: any) => {
      Message.error(err?.msg || '删除离线日志导入信息失败')
    },
  })
  return [run, loading] as const
}
